<script setup lang="ts">
/**
 * Recursive tree-view JSON renderer. Replaces the legacy Angular
 * `<json-formatter>` (from the `jsonFormatter` module) used in the lab-orders
 * JSON modal.
 *
 * Expandable object/array nodes, type-coloured leaves, key search via
 * `defaultOpen` depth. Keep this dep-free so the modal stays cheap to mount.
 */
import { computed, ref } from 'vue';

interface Props {
  value: unknown;
  /** Render as `key: …` when the node has a parent key. Internal — set by recursion. */
  name?: string | number | undefined;
  /** Auto-expand nodes up to this depth. 0 = collapse everything. */
  defaultOpen?: number | undefined;
  /** Current depth — internal, used by the recursion. */
  depth?: number | undefined;
}
const props = withDefaults(defineProps<Props>(), {
  defaultOpen: 1,
  depth: 0,
  name: undefined,
});

const open = ref<boolean>(props.depth < props.defaultOpen);

type ValueKind = 'object' | 'array' | 'string' | 'number' | 'boolean' | 'null' | 'undefined';

const kind = computed<ValueKind>(() => {
  if (props.value === null) return 'null';
  if (props.value === undefined) return 'undefined';
  if (Array.isArray(props.value)) return 'array';
  if (typeof props.value === 'object') return 'object';
  return typeof props.value as ValueKind;
});

const isContainer = computed(() => kind.value === 'object' || kind.value === 'array');

const entries = computed<Array<[string | number, unknown]>>(() => {
  if (kind.value === 'array') {
    return (props.value as unknown[]).map((v, i) => [i, v] as [number, unknown]);
  }
  if (kind.value === 'object') {
    return Object.entries(props.value as Record<string, unknown>);
  }
  return [];
});

const summary = computed(() => {
  if (kind.value === 'array') return `Array(${entries.value.length})`;
  if (kind.value === 'object') return `Object(${entries.value.length})`;
  return '';
});

function toggle(): void {
  if (isContainer.value) open.value = !open.value;
}

function quoted(s: string): string {
  return `"${s.replace(/"/g, '\\"')}"`;
}
</script>

<template>
  <div :class="['json-tree', `json-tree--${kind}`, { 'json-tree--container': isContainer }]">
    <div
      :class="['json-tree__row', { 'json-tree__row--clickable': isContainer }]"
      @click="toggle"
    >
      <span v-if="isContainer" class="json-tree__caret" :class="{ 'is-open': open }">▸</span>
      <span v-if="name !== undefined" class="json-tree__key">
        {{ typeof name === 'string' ? quoted(name) : name }}<span class="json-tree__colon">:</span>
      </span>

      <template v-if="!isContainer">
        <span v-if="kind === 'string'" class="json-tree__value json-tree__value--string">
          "{{ value }}"
        </span>
        <span v-else-if="kind === 'null'" class="json-tree__value json-tree__value--null">
          null
        </span>
        <span v-else-if="kind === 'undefined'" class="json-tree__value json-tree__value--null">
          undefined
        </span>
        <span v-else :class="['json-tree__value', `json-tree__value--${kind}`]">
          {{ value }}
        </span>
      </template>
      <span v-else class="json-tree__summary">
        {{ kind === 'array' ? '[' : '{' }}
        <span v-if="!open" class="json-tree__summary-text">{{ summary }}</span>
        <span v-if="!open">{{ kind === 'array' ? ']' : '}' }}</span>
      </span>
    </div>

    <ul v-if="isContainer && open" class="json-tree__children">
      <li v-for="[k, v] in entries" :key="String(k)">
        <JsonTree :value="v" :name="k" :default-open="defaultOpen" :depth="(depth ?? 0) + 1" />
      </li>
    </ul>
    <div v-if="isContainer && open" class="json-tree__close-bracket">
      {{ kind === 'array' ? ']' : '}' }}
    </div>
  </div>
</template>

<style scoped>
.json-tree {
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #1f2937;
}
.json-tree__row {
  display: inline-flex;
  align-items: baseline;
  gap: 0.25rem;
  padding: 0.0625rem 0;
}
.json-tree__row--clickable {
  cursor: pointer;
  user-select: none;
}
.json-tree__row--clickable:hover {
  background: #f3f4f6;
  border-radius: 0.125rem;
}
.json-tree__caret {
  display: inline-block;
  width: 0.875rem;
  color: #6b7280;
  font-size: 0.625rem;
  transition: transform 120ms ease;
}
.json-tree__caret.is-open {
  transform: rotate(90deg);
}
.json-tree__key {
  color: #1d4ed8;
  font-weight: 600;
}
.json-tree__colon {
  color: #6b7280;
  margin-right: 0.25rem;
}
.json-tree__summary {
  color: #6b7280;
}
.json-tree__summary-text {
  margin: 0 0.25rem;
  font-style: italic;
}
.json-tree__value--string {
  color: #166534;
}
.json-tree__value--number {
  color: #92400e;
}
.json-tree__value--boolean {
  color: #6d28d9;
  font-weight: 600;
}
.json-tree__value--null {
  color: #9ca3af;
  font-style: italic;
}
.json-tree__children {
  list-style: none;
  margin: 0;
  padding: 0 0 0 1.25rem;
  border-left: 1px dashed #e5e7eb;
}
.json-tree__close-bracket {
  color: #6b7280;
}
</style>
